import React, { useState, useEffect, useMemo, useRef } from "react";
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  CloseButton,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  VStack,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { FaRegQuestionCircle } from "react-icons/fa";
import {
  ProductionCapacityBenchmark,
  BenchmarkStateData,
  BenchmarkDataByPartNo,
  UniqueProcessType,
  PagePermissions,
  Errors,
  NonWorkingHoursScenario,
  BenchmarkValues,
} from "../../contexts/types";
import CeasTimePicker from "./CeasTimePicker";
import { hourMinutesStringToMinutes, resolveDisplayPartNoByKey } from "../../utils/utils";
import { codeToNameMap, ShiftSystem } from "../../contexts/constants";
import PastBenchmarkSearchPanel from "../Modal/PastBenchmarkSearchPanel";
import HelpIcon from "./HelpIcon";

/**
 * ベンチマーク入力Popoverのプロパティ
 */
interface Props {
  /** 設備orライン */
  inputType: "equipment" | "line";
  /** 設備ID（設備入力の場合） */
  equipmentId?: string;
  /** 設備名（設備入力の場合） */
  equipmentName?: string;
  /** ラインID（ライン入力の場合） */
  productionLineId?: string;
  /** ライン名（ライン入力の場合） */
  productionLineName?: string;
  /** 既存のベンチマークデータ */
  benchmarkData?: ProductionCapacityBenchmark | null;
  /** プロセスコードデータ */
  processCodeData: UniqueProcessType[];
  /**保存ボタン活性制御 */
  isSave: boolean;
  /** 保存ハンドラー */
  onSave: (data: ProductionCapacityBenchmark | undefined) => Promise<boolean>;
  /** 画面パーミッション */
  pagePermissions?: PagePermissions;
  /** 更新日時 */
  updateDate?: string | null;
  /** 稼働人数 （設備入力の場合）*/
  totalNumberOfPeople?: number;
  /** 過去稼働人数（設備入力の場合）*/
  pastTotalNumberOfPeople?: number;
  /**BOM C/T */
  pastUniqueProcess: UniqueProcessType | undefined;
  /**ライン情報（ライン入力の場合） */
  productionLineData?: any;
  /**過去ライン情報（ライン入力の場合） */
  pastProductionLineData?: any;
  /**稼働人数合計（ライン入力の場合） */
  totalPeopleRequired?: number;
  /**過去稼働人数合計（ライン入力の場合） */
  pastTotalPeopleRequired?: number;
  /** PartNoデータ（displayPartNo解決用、No.435対応） */
  partNoData?: { partNo: string; displayPartNo?: string }[];
}

/**
 * ベンチマーク入力Popover
 */
const BenchmarkInputPopover: React.FC<Props> = ({
  inputType,
  equipmentId,
  equipmentName,
  productionLineId,
  productionLineName,
  benchmarkData,
  processCodeData,
  isSave,
  onSave,
  pagePermissions,
  updateDate,
  totalNumberOfPeople,
  pastTotalNumberOfPeople,
  pastUniqueProcess,
  productionLineData,
  pastProductionLineData,
  totalPeopleRequired,
  pastTotalPeopleRequired,
  partNoData,
}) => {

  const { t } = useTranslation();
  const BP = 'features.InvestmentScreening.BenchmarkInputPopover';
  const defaultBenchmark: ProductionCapacityBenchmark = {
    inputType: "equipment",
    equipmentId: equipmentId,
    equipmentName: equipmentName,
    productionLineId: productionLineId,
    benchmarkMonth: "",
    fixedAssetNumber: "",
    processCode: "",
    operatingDays: 0,
    actualBest: {
      yield: undefined,
      operatingRate: undefined,
      cycleTime: undefined,
      personnelPerDay: undefined,
      productionVolume: undefined,
      productionPerEquipment: undefined,
      productionPerPerson: undefined,
    },
    theoretical: {
      yield: undefined,
      operatingRate: undefined,
      cycleTime: undefined,
      personnelPerDay: pastTotalNumberOfPeople || undefined,
      productionVolume: undefined,
      productionPerEquipment: undefined,
      productionPerPerson: undefined,
    },
    investmentPlan: {
      yield: productionLineData?.yield || undefined,
      operatingRate: undefined,
      cycleTime: productionLineData?.cycleTime || undefined,
      personnelPerDay: totalNumberOfPeople,
      productionVolume: undefined,
      productionPerEquipment: undefined,
      productionPerPerson: undefined,
    },
    gapAnalysis: "",
    productionPerEquipment: "",
    productionPerPerson: "",
    nonOperationMeasures: "",
    yieldMeasures: "",
    freeInputFields: {},
    workTime: "00:00",
    shiftSystem: "1",
  };
  /** ローカルベンチマークデータ */
  const [localBenchmark, setLocalBenchmark] = useState<ProductionCapacityBenchmark>(defaultBenchmark);

  /** 検索フローティングパネル表示フラグ */
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);

  const TITLE = t(`${BP}.title`)

  /** トースト表示用 */
  const toast = useToast();
  /** Popoverの開閉状態 */
  const [isOpen, setIsOpen] = useState(false);
  /**保存ボタン活性制御 */
  const isSaving = isSave;

  /** ポップアップ横幅 */
  const POPUP_WIDTH = "90vw";
  /** ポップアップ縦幅 */
  const POPUP_HEIGHT = "95vh";

  /** デフォルト値 */
  const DEFAULT_TIME = "00:00";


  /** エラー情報（デフォルト値） */
  const defaultErrors = {
    // プロジェクト名
    projectName: {
      isError: false,
      errorMessage: ""
    },
    // 設備ID
    equipmentId: {
      isError: false,
      errorMessage: ""
    },
    // 設備名
    equipmentName: {
      isError: false,
      errorMessage: ""
    },
    // ラインID
    productionLineId: {
      isError: false,
      errorMessage: ""
    },
    // ライン名
    productionLineName: {
      isError: false,
      errorMessage: ""
    },
    // 固定資産番号
    fixedAssetNumber: {
      isError: false,
      errorMessage: ""
    },
    // 過去ベスト（月）
    benchmarkMonth: {
      isError: false,
      errorMessage: ""
    },
    // BOM上プロセスコード
    processCode: {
      isError: false,
      errorMessage: ""
    },
    // 稼働日数
    operatingDays: {
      isError: false,
      errorMessage: ""
    },
    // PartNoバリデーション（PartNoモード時の未入力チェック）
    partNoValidation: {
      isError: false,
      errorMessage: ""
    },
    // 歩留(実績過去ベスト)
    actualBestYield: {
      isError: false,
      errorMessage: ""
    },
    // 歩留(理論値)
    theoreticalYield: {
      isError: false,
      errorMessage: ""
    },
    // 歩留(目論見)
    investmentPlanYield: {
      isError: false,
      errorMessage: ""
    },
    // 稼働率(実績過去ベスト)
    actualBestOperatingRate: {
      isError: false,
      errorMessage: ""
    },
    // 稼働率(理論値)
    theoreticalOperatingRate: {
      isError: false,
      errorMessage: ""
    },
    // 稼働率(目論見)
    investmentPlanOperatingRate: {
      isError: false,
      errorMessage: ""
    },
    // C/T(実績過去ベスト)
    actualBestCycleTime: {
      isError: false,
      errorMessage: ""
    },
    // C/T(理論値)
    theoreticalCycleTime: {
      isError: false,
      errorMessage: ""
    },
    // C/T(目論見)
    investmentPlanCycleTime: {
      isError: false,
      errorMessage: ""
    },
    // 一人あたり必要人員数(実績過去ベスト)
    actualBestPersonnelPerDay: {
      isError: false,
      errorMessage: ""
    },
    // 一人あたり必要人員数(理論値)
    theoreticalPersonnelPerDay: {
      isError: false,
      errorMessage: ""
    },
    // 一人あたり必要人員数(目論見)
    investmentPlanPersonnelPerDay: {
      isError: false,
      errorMessage: ""
    },
    // ギャップ分析
    gapAnalysis: {
      isError: false,
      errorMessage: ""
    },
    // 生産性向上対策
    productionPerEquipment: {
      isError: false,
      errorMessage: ""
    },
    // 1人あたり生産性向上対策
    productionPerPerson: {
      isError: false,
      errorMessage: ""
    },
    // 非稼働対策
    nonOperationMeasures: {
      isError: false,
      errorMessage: ""
    },
    // 歩留向上対策
    yieldMeasures: {
      isError: false,
      errorMessage: ""
    },
    // 労働時間
    workTime: {
      isError: false,
      errorMessage: "",
    },
    // 勤務体制
    shiftSystem: {
      isError: false,
      errorMessage: "",
    },
  };

  /** エラー情報 */
  const [errors, setErrors] = useState<Errors>(JSON.parse(JSON.stringify(defaultErrors)));
  /** PartNo別エラー情報（更新前後サマリーと同じパターン） */
  const [errorsByPartNo, setErrorsByPartNo] = useState<{ [partNo: string]: Partial<Errors> }>({});


  /**
   * 保存済みデータ（`benchmarkData`）から `localBenchmark` の初期値を構築する。
   * `benchmarkData` が無い場合は `defaultBenchmark` を返す。
   * 「開く」「閉じる」双方のタイミングで未編集状態へリセットするために共通化している。
   */
  const buildInitialLocalBenchmark = (): ProductionCapacityBenchmark => {
    if (benchmarkData) {
      // 既定値をマージしてから保存済みデータで上書きする。
      // 勤務体制・労働時間など、項目追加前に保存されたデータでは値が欠けていることがあり、
      // 画面には既定値が表示されるのに実データが未設定のままとなって保存できなくなるため。
      let initialData = { ...defaultBenchmark, ...benchmarkData };
      const originalSelectedPartNo = benchmarkData.selectedPartNo;

      // 全PartNoの計算値を再算出（更新前後サマリーと同じパターン）
      if (initialData.benchmarkByPartNo) {
        const allPartNos = Object.keys(initialData.benchmarkByPartNo);
        allPartNos.forEach((partNo) => {
          (["actualBest", "theoretical", "investmentPlan"] as const).forEach((stateType) => {
            initialData = calculateBenchmarkForPartNo(initialData, partNo, stateType);
          });
        });
      }

      // selectedPartNoを元に戻す
      initialData.selectedPartNo = originalSelectedPartNo;
      return initialData;
    }
    return defaultBenchmark;
  };

  /**
   * 初期化: 既存データがあればセット（全PartNo再計算を含む）
   */
  useEffect(() => {
    if (benchmarkData) {
      setLocalBenchmark(buildInitialLocalBenchmark());
    }
  }, [benchmarkData]);

  /**
   * フィールド更新ハンドラー
   */
  const handleUpdate = (field: string, value: any) => {
    setLocalBenchmark((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  /**
   * 指定された年月の日数を取得
   */
  const getDaysInMonth = (yearMonth: string): number => {
    if (!yearMonth) return 31;
    const [year, month] = yearMonth.split("-").map(Number);
    return new Date(year, month, 0).getDate();
  };

  /**
   * 過去ベスト月の最大稼働日数
   */
  const maxOperatingDays = getDaysInMonth(localBenchmark.benchmarkMonth || "");

  /**
   * 状態別データ更新ハンドラー（PartNo対応）
   * selectedPartNo + benchmarkByPartNo が存在する場合はPartNo別データを更新
   */
  const handleStateUpdate = (
    stateType: "actualBest" | "theoretical" | "investmentPlan",
    field: keyof BenchmarkStateData,
    value: any
  ) => {
    setLocalBenchmark((prev) => {
      const selectedPartNo = prev.selectedPartNo;
      if (selectedPartNo && prev.benchmarkByPartNo) {
        // PartNoモード: benchmarkByPartNo[selectedPartNo] を更新
        const benchmarkByPartNo = { ...prev.benchmarkByPartNo };
        const partNoData = { ...(benchmarkByPartNo[selectedPartNo] || { actualBest: {}, theoretical: {}, investmentPlan: {} }) };
        partNoData[stateType] = { ...partNoData[stateType], [field]: value };
        benchmarkByPartNo[selectedPartNo] = partNoData;
        const updated = { ...prev, benchmarkByPartNo };
        return calculateBenchmarkForPartNo(updated, selectedPartNo, stateType);
      } else {
        // ライン入力モード: トップレベルを直接更新
        const updatedState = { ...prev[stateType], [field]: value };
        const updated = { ...prev, [stateType]: updatedState };
        return calculateBenchmark(updated, stateType);
      }
    });
  };

  /**
   * フリー入力フィールド更新ハンドラー
   */
  const handleFreeFieldUpdate = (field: string, value: string) => {
    setLocalBenchmark((prev) => ({
      ...prev,
      freeInputFields: {
        ...prev.freeInputFields,
        [field]: value,
      },
    }));
  };

  /**
   * ベンチマーク計算
   */
  const calculateBenchmark = (
    benchmark: ProductionCapacityBenchmark,
    stateType: "actualBest" | "theoretical" | "investmentPlan"
  ): ProductionCapacityBenchmark => {
    const state = benchmark[stateType];
    if (!state) return benchmark;

    const { yield: yieldRate, operatingRate, cycleTime, personnelPerDay } = state;
    const { operatingDays } = benchmark;
    const workTime = hourMinutesStringToMinutes(benchmark.workTime) / 60;
    const shiftSystem = Number(benchmark.shiftSystem);

    // 生産数 = (稼働日数 × 24 × 3600) / C/T × 稼働率 × 歩留
    let productionVolume = 0;
    if (cycleTime && cycleTime > 0 && yieldRate && operatingRate && operatingDays) {
      productionVolume = Math.round(
        ((operatingDays * 24 * 3600) / cycleTime) *
        (operatingRate / 100) *
        (yieldRate / 100)
      );
    }

    // 設備あたり生産性 = 生産数 / 稼働日数 / 24
    let productionPerEquipment = 0;
    if (productionVolume > 0 && operatingDays && operatingDays > 0) {
      productionPerEquipment = Math.round(productionVolume / operatingDays / 24);
    }

    // // 1人あたり生産性 = （生産数　/　稼働日数　/　（シフト数＊労働時間））/シフト数＊人数
    let productionPerPerson = 0;
    productionPerPerson = Math.round(
      ((productionVolume / operatingDays / (shiftSystem * workTime)) /
        (shiftSystem * (personnelPerDay ?? 0))) * 100
    ) / 100;

    return {
      ...benchmark,
      [stateType]: {
        ...state,
        productionVolume,
        productionPerEquipment,
        productionPerPerson,
      },
    };
  };


  /**
   * PartNo別ベンチマーク計算
   * benchmarkByPartNo 内の指定PartNo・stateType の計算値を更新
   */
  const calculateBenchmarkForPartNo = (
    benchmark: ProductionCapacityBenchmark,
    partNo: string,
    stateType: "actualBest" | "theoretical" | "investmentPlan"
  ): ProductionCapacityBenchmark => {
    if (!benchmark.benchmarkByPartNo?.[partNo]) return benchmark;

    const state = benchmark.benchmarkByPartNo[partNo][stateType];
    if (!state) return benchmark;

    const { yield: yieldRate, operatingRate, cycleTime, personnelPerDay } = state;
    const { operatingDays } = benchmark;
    const workTime = hourMinutesStringToMinutes(benchmark.workTime) / 60;
    const shiftSystem = Number(benchmark.shiftSystem);

    let productionVolume = 0;
    if (cycleTime && cycleTime > 0 && yieldRate && operatingRate && operatingDays) {
      productionVolume = Math.round(
        ((operatingDays * 24 * 3600) / cycleTime) *
        (operatingRate / 100) *
        (yieldRate / 100)
      );
    }

    let productionPerEquipment = 0;
    if (productionVolume > 0 && operatingDays && operatingDays > 0) {
      productionPerEquipment = Math.round(productionVolume / operatingDays / 24);
    }

    let productionPerPerson = 0;
    productionPerPerson = Math.round(
      ((productionVolume / operatingDays / (shiftSystem * workTime)) /
        (shiftSystem * (personnelPerDay ?? 0))) * 100
    ) / 100;

    return {
      ...benchmark,
      benchmarkByPartNo: {
        ...benchmark.benchmarkByPartNo,
        [partNo]: {
          ...benchmark.benchmarkByPartNo[partNo],
          [stateType]: {
            ...state,
            productionVolume,
            productionPerEquipment,
            productionPerPerson,
          },
        },
      },
    };
  };

  /**
   * 一時保存ハンドラー
   */
  const handleTempSave = () => {
    const nameId = ((equipmentName || productionLineName) ?? "unknown").replace(/[\\/:*?"<>|]/g, "_");
    const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    const json = JSON.stringify({ screenName: "BenchmarkInputPopover", nameId, savedAt: new Date().toISOString(), data: localBenchmark }, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `BenchmarkInputPopover_${nameId}_${dateStr}.data`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: TITLE, description: t(`${BP}.message.tempSaved`), status: "success", duration: 3000, isClosable: true });
  };

  /**
   * 一時保存読み込みハンドラー
   */
  const handleTempLoad = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".data";
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const parsed = JSON.parse(ev.target?.result as string);
          if (parsed.screenName !== "BenchmarkInputPopover") {
            toast({ title: TITLE, description: t(`${BP}.message.tempLoadScreenMismatch`), status: "error", duration: 4000, isClosable: true });
            return;
          }
          if (inputType !== parsed.data.inputType) {
            if (inputType === "line") {
              toast({title: TITLE, description: t(`${BP}.message.tempLoadForWholeLineMismatch`), status: "error", duration: 4000, isClosable: true }); // 英語予定　This file is not for the whole line.
            } else if (inputType === "equipment") {
              toast({title: TITLE, description: t(`${BP}.message.tempLoadForEquipmentExpansionMismatch`), status: "error", duration: 4000, isClosable: true }); // 英語予定　This file is not for equipment expansion.
            }
            return;
          }
          setLocalBenchmark({
            ...parsed.data,
            equipmentId,
            equipmentName,
            productionLineId,
          });
          toast({ title: TITLE, description: t(`${BP}.message.tempLoaded`), status: "success", duration: 3000, isClosable: true });
        } catch {
          toast({ title: TITLE, description: t(`${BP}.message.tempLoadFailed`), status: "error", duration: 4000, isClosable: true });
        }
      };
      reader.readAsText(file);
    };
    input.click();
  };

  /**
   * 保存ハンドラー
   */
  const handleSave = async () => {
    let dataToSave = { ...localBenchmark };

    // ライン名はrdsに保存させず、Dynamoから参照するように変更に伴い既存のものも削除
    delete dataToSave.productionLineName;
    // PartNoモードの場合はトップレベルのベンチマークデータをクリア（benchmarkByPartNoに一本化）
    if (dataToSave.benchmarkByPartNo && Object.keys(dataToSave.benchmarkByPartNo).length > 0) {
      dataToSave = {
        ...dataToSave,
        actualBest: {},
        theoretical: {},
        investmentPlan: {},
      };
    }
    const result = await onSave(dataToSave);
    if (result) {
      // 保存成功時は、props（benchmarkData）を使った状態の再構築は行わない。
      // 親コンポーネント側の状態更新タイミングによっては、保存直後のbenchmarkData propsが
      // まだ更新前の値のままのことがあり、再構築すると保存前の値へ巻き戻ってしまうため、
      // 保存した内容そのもの（dataToSave）を明示的に反映する。
      closePopover();
      setLocalBenchmark(dataToSave);
    }
  }

  /** Popoverを閉じる（入力状態のリセットは行わない） */
  const closePopover = () => {
    setIsOpen(false);
    setIsSearchModalOpen(false);
  };

  /** メインモーダルを閉じる（未保存の編集内容は破棄し、保存済みデータの状態へリセットする） */
  const handleModalClose = () => {
    closePopover();
    setLocalBenchmark(buildInitialLocalBenchmark());
    setErrors(JSON.parse(JSON.stringify(defaultErrors)));
    setErrorsByPartNo({});
  };

  /**
   * 数値フォーマット
   */
  const formatNumber = (value: number | undefined): string => {
    if (value === undefined || value === null) return "";
    return value.toLocaleString();
  };
  // 差異計算
  const getDifference = (investmentPlan?: number, actualBest?: number, theoretical?: number) => {
    const diff = Number(investmentPlan) - Math.max(Number(actualBest), Number(theoretical));
    return isNaN(diff) ? 0 : diff;
  }
  // 差異計算(C/T)
  const getCicleTimeDifference = (investmentPlan?: number, actualBest?: number, theoretical?: number) => {
    const diff = Number(investmentPlan) - Math.min(Number(actualBest), Number(theoretical));
    return isNaN(diff) ? 0 : diff;
  }



  // エラーチェック
  // 関数宣言（エラー情報設定）
  const changeErrors = (name: string, errorMessage: string) => {
    setErrors((prevState) => ({
      ...prevState,
      [name]: { isError: true, errorMessage: `${errorMessage}` },
    }))
    isError = true;
  };

  let isError = false;

  /**
    * バリデーションを行う。
    */
  const validate = () => {
    // 必須入力チェック
    const required = (value: unknown) => {
      if (value === null || value === undefined) return false;

      const str = String(value).trim();
      if (str === "NaN") return false;
      return str !== "" && str !== "0" && str !== "０";
    };

    // エラー情報初期化
    setErrors(JSON.parse(JSON.stringify(defaultErrors)));
    setErrorsByPartNo({});

    let isError = false;
    // 変数宣言
    // エラーメッセージを初期化
    setErrors(JSON.parse(JSON.stringify(defaultErrors)));

    // 固定資産番号
    if (!required(localBenchmark?.fixedAssetNumber ?? "")) {
      changeErrors("fixedAssetNumber", t(`${BP}.validation.fixedAssetNumber`));
      isError = true;
    }

    // BOM上プロセスコード
    if (!productionLineId) {
      if (!required(localBenchmark?.processCode ?? "")) {
        changeErrors("processCode", t(`${BP}.validation.processCode`));
        isError = true;
      }
    }

    // 過去ベスト
    if (!required(localBenchmark?.benchmarkMonth ?? "")) {
      changeErrors("benchmarkMonth", t(`${BP}.validation.benchmarkMonth`));
      isError = true;
    }
    // 勤務体制は手入力を正とする方針に変更したため、
    // 「選択月に非稼働時間データ（シフト情報）が存在するか」のチェックは行わない
    // （勤務体制自体の未入力チェックは後段で実施している）
    // 稼働日数
    if (!required(String(localBenchmark.operatingDays) ?? "")) {
      changeErrors("operatingDays", t(`${BP}.validation.operatingDays`));
      isError = true;
    }

    // PartNoモード: 全PartNoをチェックし、PartNo別エラーMapを構築（更新前後サマリーと同じパターン）
    if (hasPartNo && localBenchmark.benchmarkByPartNo) {
      const missingPartNos: string[] = [];
      const allPartNos = Object.keys(localBenchmark.benchmarkByPartNo);
      const newErrorsByPartNo: { [partNo: string]: Partial<Errors> } = {};

      for (const partNo of allPartNos) {
        const data = localBenchmark.benchmarkByPartNo[partNo];
        if (!data) { missingPartNos.push(partNo); continue; }

        let partNoHasError = false;
        const partNoErrors: Partial<Errors> = {};

        // 歩留
        if (!required(data.actualBest?.yield ?? "")) { partNoHasError = true; partNoErrors.actualBestYield = { isError: true, errorMessage: t(`${BP}.validation.yield`) }; }
        if (!required(data.theoretical?.yield ?? "")) { partNoHasError = true; partNoErrors.theoreticalYield = { isError: true, errorMessage: t(`${BP}.validation.yield`) }; }
        if (!required(data.investmentPlan?.yield ?? "")) { partNoHasError = true; partNoErrors.investmentPlanYield = { isError: true, errorMessage: t(`${BP}.validation.yield`) }; }
        // 稼働率
        if (!required(data.actualBest?.operatingRate ?? "")) { partNoHasError = true; partNoErrors.actualBestOperatingRate = { isError: true, errorMessage: t(`${BP}.validation.operatingRate`) }; }
        if (!required(data.theoretical?.operatingRate ?? "")) { partNoHasError = true; partNoErrors.theoreticalOperatingRate = { isError: true, errorMessage: t(`${BP}.validation.operatingRate`) }; }
        if (!required(data.investmentPlan?.operatingRate ?? "")) { partNoHasError = true; partNoErrors.investmentPlanOperatingRate = { isError: true, errorMessage: t(`${BP}.validation.operatingRate`) }; }
        // C/T
        if (!required(data.actualBest?.cycleTime ?? "")) { partNoHasError = true; partNoErrors.actualBestCycleTime = { isError: true, errorMessage: t(`${BP}.validation.cycleTime`) }; }
        if (!required(data.theoretical?.cycleTime ?? "")) { partNoHasError = true; partNoErrors.theoreticalCycleTime = { isError: true, errorMessage: t(`${BP}.validation.cycleTime`) }; }
        if (!required(data.investmentPlan?.cycleTime ?? "")) { partNoHasError = true; partNoErrors.investmentPlanCycleTime = { isError: true, errorMessage: t(`${BP}.validation.cycleTime`) }; }
        // 人員数
        if (!required(data.actualBest?.personnelPerDay ?? "")) { partNoHasError = true; partNoErrors.actualBestPersonnelPerDay = { isError: true, errorMessage: t(`${BP}.validation.personnelPerDay`) }; }
        if (!required(data.theoretical?.personnelPerDay ?? "")) { partNoHasError = true; partNoErrors.theoreticalPersonnelPerDay = { isError: true, errorMessage: t(`${BP}.validation.personnelPerDay`) }; }
        if (!required(data.investmentPlan?.personnelPerDay ?? "")) { partNoHasError = true; partNoErrors.investmentPlanPersonnelPerDay = { isError: true, errorMessage: t(`${BP}.validation.personnelPerDay`) }; }

        if (partNoHasError) missingPartNos.push(partNo);
        newErrorsByPartNo[partNo] = partNoErrors;
      }

      setErrorsByPartNo(newErrorsByPartNo);

      if (missingPartNos.length > 0) {
        changeErrors("partNoValidation", t(`${BP}.validation.partNoMissing`, { partNos: missingPartNos.join(", ") }));
        isError = true;
      }
    }

    // 選択中データの項目別チェック（ライン入力モードのみ。PartNoモードはerrorsByPartNoから参照）
    if (!hasPartNo) {
      // 歩留(実績過去ベスト)
      if (!required(displayActualBest?.yield ?? "")) {
        changeErrors("actualBestYield", t(`${BP}.validation.yield`));
        isError = true;
      }

      // 歩留(理論値)
      if (!required(displayTheoretical?.yield ?? "")) {
        changeErrors("theoreticalYield", t(`${BP}.validation.yield`));
        isError = true;
      }

      // 歩留(目論見)
      if (!required(displayInvestmentPlan?.yield ?? "")) {
        changeErrors("investmentPlanYield", t(`${BP}.validation.yield`));
        isError = true;
      }

      // 稼働率(実績過去ベスト)
      if (!required(displayActualBest?.operatingRate ?? "")) {
        changeErrors("actualBestOperatingRate", t(`${BP}.validation.operatingRate`));
        isError = true;
      }

      // 稼働率(理論値)
      if (!required(displayTheoretical?.operatingRate ?? "")) {
        changeErrors("theoreticalOperatingRate", t(`${BP}.validation.operatingRate`));
        isError = true;
      }

      // 稼働率(目論見)
      if (!required(displayInvestmentPlan?.operatingRate ?? "")) {
        changeErrors("investmentPlanOperatingRate", t(`${BP}.validation.operatingRate`));
        isError = true;
      }

      // C/T(実績過去ベスト)
      if (!required(displayActualBest?.cycleTime ?? "")) {
        changeErrors("actualBestCycleTime", t(`${BP}.validation.cycleTime`));
        isError = true;
      }

      // C/T(理論値)
      if (!required(displayTheoretical?.cycleTime ?? "")) {
        changeErrors("theoreticalCycleTime", t(`${BP}.validation.cycleTime`));
        isError = true;
      }

      // C/T(目論見)
      if (!required(displayInvestmentPlan?.cycleTime ?? "")) {
        changeErrors("investmentPlanCycleTime", t(`${BP}.validation.cycleTime`));
        isError = true;
      }

      // 1日あたり必要人員数(実績過去ベスト)
      if (!required(displayActualBest?.personnelPerDay ?? "")) {
        changeErrors("actualBestPersonnelPerDay", t(`${BP}.validation.personnelPerDay`));
        isError = true;
      }

      // 1日あたり必要人員数(理論値)
      if (!required(displayTheoretical?.personnelPerDay ?? "")) {
        changeErrors("theoreticalPersonnelPerDay", t(`${BP}.validation.personnelPerDay`));
        isError = true;
      }

      // 1日あたり必要人員数(目論見)
      if (!required(displayInvestmentPlan?.personnelPerDay ?? "")) {
        changeErrors("investmentPlanPersonnelPerDay", t(`${BP}.validation.personnelPerDay`));
        isError = true;
      }
    }

    // ギャップ分析
    if (!required(localBenchmark.freeInputFields?.gapAnalysis ?? "")) {
      changeErrors("gapAnalysis", t(`${BP}.validation.gapAnalysis`));
      isError = true;
    }

    // 設備あたり生産性向上対策
    if (!required(localBenchmark.freeInputFields?.productionPerEquipment ?? "")) {
      changeErrors("productionPerEquipment", t(`${BP}.validation.productionPerEquipment`));
      isError = true;
    }

    // 1人あたり生産性向上対策
    if (!required(localBenchmark.freeInputFields?.productionPerPerson ?? "")) {
      changeErrors("productionPerPerson", t(`${BP}.validation.productionPerPerson`));
      isError = true;
    }

    // 非稼働対策
    if (!required(localBenchmark.freeInputFields?.nonOperationMeasures ?? "")) {
      changeErrors("nonOperationMeasures", t(`${BP}.validation.nonOperationMeasures`));
      isError = true;
    }

    // 歩留向上対策
    if (!required(localBenchmark.freeInputFields?.yieldMeasures ?? "")) {
      changeErrors("yieldMeasures", t(`${BP}.validation.yieldMeasures`));
      isError = true;
    }

    // 労働時間
    if (!required(localBenchmark?.workTime === DEFAULT_TIME ? "" : (localBenchmark?.workTime ?? ""))) {
      changeErrors("workTime", t(`${BP}.validation.workTime`));
      isError = true;
    }
    // 勤務体制
    if (!required(localBenchmark?.shiftSystem ?? "")) {
      changeErrors("shiftSystem", t(`${BP}.validation.shiftSystem`));
      isError = true;
    }
    return !isError;
  }

  // 過去ベスト（月）は手入力を正とする方針のため、
  // 非稼働時間データから選択可能範囲（カレンダーのmin/max）を算出する処理は廃止した

  /**
 * BOM（processId）に対応するC/Tを取得する関数。
 * 
 * @param processes - r_ceas_part_no_and_processのデータ
 * @param targetId - BOM上プロセスコード
 * @returns 該当する工程のC/T。取得できない場合は 0。
 */
  const getCycleTimeByProcessId = (processes: any, targetId: string): number => {
    if (!processes || !targetId) return 0;

    // processes がオブジェクトの場合は values のみを抽出して配列化する
    const processArray = Array.isArray(processes) ? processes : Object.values(processes);
    // uniqueProcessId が targetId と一致する工程を検索
    const targetItem = processArray.find((item: any) => item.uniqueProcessId === targetId);
    if (!targetItem) return 0;

    try {
      // JSON文字列をパースして配列形式のデータを取得
      const parsed = JSON.parse(targetItem.uniqueProcessCycleTimeInSecondByPartNo);
      if (!parsed || !parsed.length) return 0;
      // 最初の要素から cycleTime を取得して数値化
      return parseFloat(parseFloat(parsed[0].cycleTime).toFixed(2));
    } catch (e) {
      // JSON パースに失敗した場合のログ出力
      console.warn(`Failed to parse JSON for ${targetItem.uniqueProcessId}`, e);
      return 0;
    }
  };

  /**
   * PartNo別C/T取得
   */
  const getCycleTimeByProcessIdAndPartNo = (processes: any, targetId: string, partNo: string): number => {
    if (!processes || !targetId || !partNo) return 0;
    const processArray = Array.isArray(processes) ? processes : Object.values(processes);
    const targetItem = processArray.find((item: any) => item.uniqueProcessId === targetId);
    if (!targetItem?.uniqueProcessCycleTimeInSecondByPartNo) return 0;
    try {
      const parsed = JSON.parse(targetItem.uniqueProcessCycleTimeInSecondByPartNo);
      const match = (parsed || []).find((item: any) => item.partNo === partNo);
      return match ? parseFloat(parseFloat(match.cycleTime).toFixed(2)) : 0;
    } catch {
      return 0;
    }
  };

  /**
   * 選択中のプロセスコードからPartNoリストを取得
   */
  const partNoList = useMemo((): { partNo: string; cycleTime: number }[] => {
    if (!localBenchmark.processCode || !processCodeData) return [];
    const targetProcess = processCodeData.find(p => p.uniqueProcessId === localBenchmark.processCode);
    if (!targetProcess?.uniqueProcessCycleTimeInSecondByPartNo) return [];
    try {
      const parsed = JSON.parse(targetProcess.uniqueProcessCycleTimeInSecondByPartNo);
      return (parsed || [])
        .map((item: any) => ({
          partNo: item.partNo || "",
          cycleTime: parseFloat(parseFloat(item.cycleTime).toFixed(2)) || 0,
        }))
        .filter((item: { partNo: string }) => item.partNo !== "");
    } catch {
      return [];
    }
  }, [localBenchmark.processCode, processCodeData]);

  /**
   * processCode変更時にPartNo別benchmarkByPartNoを初期化
   */
  useEffect(() => {
    if (partNoList.length === 0) return;

    setLocalBenchmark((prev) => {
      const existingData = prev.benchmarkByPartNo || {};
      const result: { [partNo: string]: BenchmarkDataByPartNo } = {};

      // レガシーデータ検出: benchmarkByPartNoが空でトップレベルにデータがある場合、最初のPartNoにマージ
      const hasLegacyTopLevel = Object.keys(existingData).length === 0 &&
        ((prev.actualBest && Object.keys(prev.actualBest).length > 0) ||
          (prev.theoretical && Object.keys(prev.theoretical).length > 0) ||
          (prev.investmentPlan && Object.keys(prev.investmentPlan).length > 0));

      partNoList.forEach(({ partNo, cycleTime }, index) => {
        if (existingData[partNo]) {
          result[partNo] = existingData[partNo];
        } else if (hasLegacyTopLevel && index === 0) {
          // レガシーデータを最初のPartNoにマージ
          result[partNo] = {
            actualBest: prev.actualBest || {},
            theoretical: prev.theoretical || {},
            investmentPlan: prev.investmentPlan || { cycleTime: cycleTime || undefined },
          };
        } else {
          result[partNo] = {
            actualBest: {},
            theoretical: {},
            investmentPlan: { cycleTime: cycleTime || undefined },
          };
        }
      });

      // selectedPartNoの初期化（既存選択がなければ空 = 「選択してください」）
      const selectedPartNo = prev.selectedPartNo && result[prev.selectedPartNo]
        ? prev.selectedPartNo
        : "";

      return { ...prev, benchmarkByPartNo: result, selectedPartNo };
    });
  }, [partNoList]);

  /**
   * 現在選択中のPartNoのデータを取得するメモ
   */
  const currentPartNoData = useMemo((): BenchmarkDataByPartNo | undefined => {
    const selectedPartNo = localBenchmark.selectedPartNo;
    if (!selectedPartNo || !localBenchmark.benchmarkByPartNo) return undefined;
    return localBenchmark.benchmarkByPartNo[selectedPartNo];
  }, [localBenchmark.selectedPartNo, localBenchmark.benchmarkByPartNo]);

  /** PartNoモードかどうか */
  const hasPartNo = partNoList.length > 0;

  /** 表示用データソース: PartNoモード時はcurrentPartNoData、それ以外はトップレベル */
  const displayActualBest = hasPartNo && currentPartNoData ? currentPartNoData.actualBest : localBenchmark.actualBest;
  const displayTheoretical = hasPartNo && currentPartNoData ? currentPartNoData.theoretical : localBenchmark.theoretical;
  const displayInvestmentPlan = hasPartNo && currentPartNoData ? currentPartNoData.investmentPlan : localBenchmark.investmentPlan;

  /** フィールド別エラー: PartNoモード時はerrorsByPartNoから参照、ライン入力モードはerrorsから参照 */
  const fieldErrors = useMemo((): Errors => {
    if (hasPartNo && localBenchmark.selectedPartNo && errorsByPartNo[localBenchmark.selectedPartNo]) {
      return { ...errors, ...errorsByPartNo[localBenchmark.selectedPartNo] } as Errors;
    }
    return errors;
  }, [hasPartNo, localBenchmark.selectedPartNo, errorsByPartNo, errors]);

  /**
   * PartNo切替ハンドラー（更新前後サマリーのhandlePartNoChangeと同じパターン）
   */
  const handlePartNoChange = (newPartNo: string) => {
    setLocalBenchmark((prev) => {
      let updated = { ...prev };

      // 新しいPartNoのデータを取得（既存 or 新規作成）
      let newPartNoData = prev.benchmarkByPartNo?.[newPartNo];
      if (!newPartNoData) {
        const cycleTime = getCycleTimeByProcessIdAndPartNo(processCodeData, prev.processCode, newPartNo);
        newPartNoData = {
          actualBest: {},
          theoretical: {},
          investmentPlan: { cycleTime: cycleTime || undefined },
        };
        updated.benchmarkByPartNo = {
          ...prev.benchmarkByPartNo,
          [newPartNo]: newPartNoData,
        };
      }

      // 選択PartNoを更新
      updated.selectedPartNo = newPartNo;

      // 全stateTypeの計算値を算出
      (["actualBest", "theoretical", "investmentPlan"] as const).forEach((stateType) => {
        updated = calculateBenchmarkForPartNo(updated, newPartNo, stateType);
      });

      return updated;
    });
  };

  // Null,Undfind,∞をチェック
  const safeNumber = (value: any, defaultValue = 0): number => {
    const num = Number(value);
    if (value == null || !Number.isFinite(num)) {
      return defaultValue;
    }
    return num;
  };

  // 稼働率は手入力を正とする方針のため、非稼働時間データから自動セットする処理は廃止した


  // -----------------------------
  // useEffect(値更新)
  // -----------------------------

  // 設備、ライン更新（DB入力チェック時必要）
  useEffect(() => {
    handleUpdate("inputType", inputType)
  }, [inputType]);

  // C/T,必要人数更新(設備選択時)
  useEffect(() => {
    if (hasPartNo && localBenchmark.selectedPartNo) {
      // PartNoモード: 選択中PartNoのC/Tを使用
      // 選択中PartNoの保存済み投資計画データ（benchmarkDataはpropsなのでクロージャの影響を受けない）
      const savedPartNoInvestmentPlan = benchmarkData?.benchmarkByPartNo?.[localBenchmark.selectedPartNo]?.investmentPlan;
      // 選択中PartNoの保存済み理論値データ（同上）
      const savedPartNoTheoretical = benchmarkData?.benchmarkByPartNo?.[localBenchmark.selectedPartNo]?.theoretical;
      if (processCodeData) {
        const cycleTime = getCycleTimeByProcessIdAndPartNo(processCodeData, localBenchmark.processCode, localBenchmark.selectedPartNo);
        // 保存済みデータがある場合は上書きしない
        if (savedPartNoInvestmentPlan?.cycleTime === undefined) {
          handleStateUpdate("investmentPlan", "cycleTime", cycleTime);
        }
        // 保存済みデータがある場合は上書きしない（0人の保存も有効な値として扱う）
        if (savedPartNoInvestmentPlan?.personnelPerDay === undefined) {
          handleStateUpdate("investmentPlan", "personnelPerDay", totalNumberOfPeople);
        }
      }
      if (pastUniqueProcess) {
        // PartNo別C/Tを取得（過去PJに該当PartNoが無い場合は空欄＝手入力）
        const pastCycleTime = getCycleTimeByProcessIdAndPartNo(pastUniqueProcess, localBenchmark.processCode, localBenchmark.selectedPartNo);
        // 保存済みデータがある場合は上書きしない（手入力を正とする。他の自動反映箇所と挙動を統一）
        if (pastCycleTime > 0 && savedPartNoTheoretical?.cycleTime === undefined) {
          handleStateUpdate("theoretical", "cycleTime", pastCycleTime);
        }
        // 保存済みデータがある場合は上書きしない（0人の保存も有効な値として扱う）
        if (pastTotalNumberOfPeople && pastTotalNumberOfPeople > 0 && savedPartNoTheoretical?.personnelPerDay === undefined) {
          handleStateUpdate("theoretical", "personnelPerDay", pastTotalNumberOfPeople);
        }
      }
    } else {
      // ライン入力モード
      if (processCodeData) {
        const cycleTime = getCycleTimeByProcessId(processCodeData, localBenchmark.processCode);
        // 保存済みデータがある場合は上書きしない（benchmarkDataはpropsなのでクロージャの影響を受けない）
        if (!benchmarkData?.investmentPlan?.cycleTime) {
          handleStateUpdate("investmentPlan", "cycleTime", cycleTime);
        }
        if (!benchmarkData?.investmentPlan?.personnelPerDay) {
          handleStateUpdate("investmentPlan", "personnelPerDay", totalNumberOfPeople);
        }
      }
      if (pastUniqueProcess) {
        const pastCycleTime = getCycleTimeByProcessId(pastUniqueProcess, localBenchmark.processCode);
        // 保存済みデータがある場合は上書きしない
        if (!benchmarkData?.theoretical?.cycleTime) {
          handleStateUpdate("theoretical", "cycleTime", pastCycleTime);
        }
        if (pastTotalNumberOfPeople && pastTotalNumberOfPeople > 0 && !benchmarkData?.theoretical?.personnelPerDay) {
          handleStateUpdate("theoretical", "personnelPerDay", pastTotalNumberOfPeople);
        }
      }
    }
  }, [localBenchmark.processCode, localBenchmark.selectedPartNo, isOpen]);

  // 歩留,C/T,必要人数更新（ライン全体選択時）
  // 反映元データ（productionLineId切替、及びそれに伴い非同期取得後に遅れて届く
  // productionLineData/pastProductionLineData等）が変化する度に再計算する。
  // isOpenのみに依存すると、開いたままライン切替やその後の非同期取得完了があっても
  // 再計算されないため、実際に参照している値を全て依存配列に含める。
  useEffect(() => {
    if (productionLineId) {
      // 理論値（保存済みデータがある場合は上書きしない、benchmarkDataはpropsなのでクロージャの影響を受けない）
      if (pastProductionLineData) {
        if (!benchmarkData?.theoretical?.yield) {
          handleStateUpdate("theoretical", "yield", pastProductionLineData?.yieldRate);
        }
        if (!benchmarkData?.theoretical?.cycleTime) {
          handleStateUpdate("theoretical", "cycleTime", pastProductionLineData?.cycleTime);
        }
        if (!benchmarkData?.theoretical?.personnelPerDay) {
          handleStateUpdate("theoretical", "personnelPerDay", pastTotalPeopleRequired);
        }
      }
      // 目論見（保存済みデータがある場合は上書きしない）
      if (productionLineData) {
        if (!benchmarkData?.investmentPlan?.yield) {
          handleStateUpdate("investmentPlan", "yield", productionLineData[0]?.yieldRate);
        }
        if (!benchmarkData?.investmentPlan?.cycleTime) {
          handleStateUpdate("investmentPlan", "cycleTime", productionLineData[0]?.cycleTime);
        }
        if (!benchmarkData?.investmentPlan?.personnelPerDay) {
          handleStateUpdate("investmentPlan", "personnelPerDay", totalPeopleRequired);
        }
      }
    } return;
  }, [isOpen, productionLineId, productionLineData, pastProductionLineData, totalPeopleRequired, pastTotalPeopleRequired]);

  // 労働時間、過去ベスト（月）更新時再計算（再描画の為）
  useEffect(() => {
    (["actualBest", "theoretical", "investmentPlan"] as const).forEach((stateType) => {
      setLocalBenchmark((prev) => {
        let updated = { ...prev };
        if (updated.benchmarkByPartNo) {
          // PartNoモード: 全PartNoを再計算
          Object.keys(updated.benchmarkByPartNo).forEach((pn) => {
            updated = calculateBenchmarkForPartNo(updated, pn, stateType);
          });
          return updated;
        } else {
          return calculateBenchmark(updated, stateType);
        }
      });
    });
  }, [localBenchmark.workTime, localBenchmark.shiftSystem]);




  /**
   * レンダリング
   */
  return (
    <>
      <Button
        size="sm"
        onClick={() => setIsOpen(true)}
        m={1}
      >
        {t(`${BP}.label.openButton`)}
      </Button>

      {isOpen && (
        <Box
          position="fixed"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          zIndex={1500}
          width={POPUP_WIDTH}
          height={POPUP_HEIGHT}
          bg="white"
          border="1px solid #ccc"
          borderRadius="md"
          shadow="lg"
          overflowY="auto"
        >
          {/* ヘッダー */}
          <Box
            p={3}
            borderBottom="1px solid"
            borderColor="gray.200"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <HStack spacing={1} align="center">
              <Text fontSize="lg" fontWeight="bold">
                {t(`${BP}.label.header`)}
                {updateDate && (
                  <Text fontSize="xs" ml={2} display="inline" color="gray.600">
                    {t(`${BP}.label.updatedAt`, { date: updateDate })}
                  </Text>
                )}
              </Text>
              <Popover placement="auto">
                <PopoverTrigger>
                  <Button
                    style={{ background: 'none', border: 'none', padding: 0 }}
                    minW="auto"
                    h="auto"
                    ml={1}
                  >
                    <FaRegQuestionCircle size="16px" cursor="pointer" />
                  </Button>
                </PopoverTrigger>
                <Portal>
                  <PopoverContent width="fit-content" maxW="750px" bgColor="#fffff0">
                    <PopoverArrow />
                    <PopoverBody>
                      <Text fontSize="sm" whiteSpace="pre-wrap">{t(`${BP}.label.headerTooltip`)}</Text>
                    </PopoverBody>
                  </PopoverContent>
                </Portal>
              </Popover>
            </HStack>
            <CloseButton onClick={handleModalClose} />
          </Box>

          {/* ボディ */}
          <Box p={6}>
            <VStack align="stretch" spacing={4}>
              {/* NumberIncrementStepper/NumberDecrementStepperはネイティブbuttonではないためfieldsetのdisabledが効かず、pointerEvents:'none'で明示的にクリック不可にする */}
              <fieldset disabled={!pagePermissions?.isEditable} style={!pagePermissions?.isEditable ? { pointerEvents: 'none' } : undefined}>
                {/* 基本情報 */}
                <Box>
                  <Text fontWeight="bold" mb={2}>{t(`${BP}.label.basicInfo`)}</Text>
                  <VStack align="stretch" spacing={2}>
                    <FormControl>
                      <HStack>
                        <FormLabel mb={0}>{t(`${BP}.label.target`)}</FormLabel>
                        <Text fontSize="md" fontWeight="semibold">
                          {equipmentId ? t(`${BP}.label.equipment`) : t(`${BP}.label.line`)}
                        </Text>
                      </HStack>
                      <Text textAlign="left" fontSize="lg" fontWeight="semibold" mt={2}>
                        {equipmentId ? equipmentName : productionLineName}
                      </Text>
                    </FormControl>
                    <HStack mt={4} spacing={4} alignItems="flex-start" >
                      <FormControl width="50%" isRequired isInvalid={!!errors.fixedAssetNumber.isError}>
                        <FormLabel>{t(`${BP}.label.fixedAssetNumber`)}</FormLabel>
                        <Input
                          value={localBenchmark.fixedAssetNumber || ""}
                          onChange={(e) => handleUpdate("fixedAssetNumber", e.target.value)}
                        />
                        <FormErrorMessage>{errors.fixedAssetNumber.errorMessage}</FormErrorMessage>
                      </FormControl>
                      {!productionLineId &&
                        <FormControl width="50%" isRequired isInvalid={!!errors.processCode.isError}>
                          <FormLabel>{t(`${BP}.label.processCode`)}</FormLabel>
                          <Select
                            value={localBenchmark.processCode || ""}
                            onChange={(e) => handleUpdate("processCode", e.target.value)}
                          >
                            <option value="">{t('common.label.selectPlaceholder')}</option>
                            {(processCodeData || []).map((process) => (
                              <option key={process.uniqueProcessId} value={process.uniqueProcessId ?? ""}>
                                {process.uniqueProcessId} - {process.uniqueProcessName}
                              </option>
                            ))}
                          </Select>
                          <FormErrorMessage>{errors.processCode.errorMessage}</FormErrorMessage>
                        </FormControl>
                      }
                    </HStack>
                    <HStack spacing={4} alignItems="flex-start">
                      <FormControl width="250px" isRequired isInvalid={!!errors.benchmarkMonth.isError}>
                        <FormLabel display="flex" alignItems="center">
                          {t(`${BP}.label.benchmarkMonth`)}
                          <HelpIcon size="20">
                            {t(`${BP}.label.benchmarkMonthTooltip`)}
                          </HelpIcon>
                        </FormLabel>
                        <Input
                          type="month"
                          value={localBenchmark.benchmarkMonth || ""}
                          onChange={(e) => {
                            handleUpdate("benchmarkMonth", e.target.value);
                            // 勤務体制・稼働率は手入力を正とする方針のため、
                            // 非稼働時間データからの自動反映（上書き）は行わない
                            // 過去ベスト変更時に各状態を再計算
                            ["actualBest", "theoretical", "investmentPlan"].forEach((stateType) => {
                              setLocalBenchmark((prev) => {
                                let updated = { ...prev, operatingDays: localBenchmark.operatingDays };
                                if (updated.benchmarkByPartNo) {
                                  Object.keys(updated.benchmarkByPartNo).forEach((pn) => {
                                    updated = calculateBenchmarkForPartNo(updated, pn, stateType as any);
                                  });
                                  return updated;
                                } else {
                                  return calculateBenchmark(updated, stateType as any);
                                }
                              });
                            });
                            // 過去ベスト月が変更されたら、稼働日数を月の日数でキャップする
                            const maxDays = getDaysInMonth(e.target.value);
                            if (localBenchmark.operatingDays && localBenchmark.operatingDays > maxDays) {
                              handleUpdate("operatingDays", maxDays);
                            }
                          }}
                        />
                        <FormErrorMessage>{errors.benchmarkMonth.errorMessage}</FormErrorMessage>
                      </FormControl>
                      <FormControl width="250px" isRequired isInvalid={!!errors.operatingDays.isError}>
                        <FormLabel>{t(`${BP}.label.operatingDays`)}</FormLabel>
                        <NumberInput
                          value={localBenchmark.operatingDays || ""}
                          onChange={(_, valueNumber) => {
                            handleUpdate("operatingDays", valueNumber);
                            // 稼働日数変更時に各状態を再計算
                            ["actualBest", "theoretical", "investmentPlan"].forEach((stateType) => {
                              setLocalBenchmark((prev) => {
                                let updated = { ...prev, operatingDays: valueNumber };
                                if (updated.benchmarkByPartNo) {
                                  Object.keys(updated.benchmarkByPartNo).forEach((pn) => {
                                    updated = calculateBenchmarkForPartNo(updated, pn, stateType as any);
                                  });
                                  return updated;
                                } else {
                                  return calculateBenchmark(updated, stateType as any);
                                }
                              });
                            });
                          }}
                          min={0}
                          max={maxOperatingDays}
                        >
                          <NumberInputField />
                          <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        </NumberInput>
                        <FormErrorMessage>{errors.operatingDays.errorMessage}</FormErrorMessage>
                      </FormControl>
                      {/* 労働時間 */}
                      <FormControl w="120px" isRequired isInvalid={!!errors.workTime.isError}>
                        <FormLabel>{t(`${BP}.label.workTime`)}</FormLabel>
                        <CeasTimePicker
                          isInvalid={!!errors.workTime.isError}
                          value={localBenchmark.workTime || DEFAULT_TIME}
                          onChange={(value) => {
                            handleUpdate("workTime", value);
                            // 労働時間変更時に各状態を再計算
                            ["actualBest", "theoretical", "investmentPlan"].forEach((stateType) => {
                              setLocalBenchmark((prev) =>
                                calculateBenchmark({ ...prev, operatingDays: localBenchmark.operatingDays }, stateType as any)
                              );
                            });
                          }}
                        />
                        <FormErrorMessage>{errors.workTime.errorMessage}</FormErrorMessage>
                      </FormControl>
                      {/* 勤務体制 */}
                      <FormControl w="150px" isRequired isInvalid={!!errors.shiftSystem.isError}>
                        <FormLabel>{t(`${BP}.label.shiftSystem`)}</FormLabel>
                        <Select
                          value={localBenchmark.shiftSystem || "1"}
                          onChange={(e) => {
                            handleUpdate("shiftSystem", e.target.value);
                            // シフト数変更時に各状態を再計算
                            ["actualBest", "theoretical", "investmentPlan"].forEach((stateType) => {
                              setLocalBenchmark((prev) =>
                                calculateBenchmark({ ...prev, operatingDays: localBenchmark.operatingDays }, stateType as any)
                              );
                            });
                          }}
                        >
                          {Object.entries(codeToNameMap(ShiftSystem))
                            // 「稼働無し」（コード"0"）は必須入力チェックで未入力扱いとなり保存できず、
                            // 生産性の計算もゼロ除算となるため選択肢から除外する
                            .filter(([code]) => code !== ShiftSystem.NoOperation.code)
                            .map(([code, name]) => (
                              <option key={code} value={code}>
                                {name}
                              </option>
                            ))}
                        </Select>
                        <FormErrorMessage>{errors.shiftSystem.errorMessage}</FormErrorMessage>
                      </FormControl>
                    </HStack>
                  </VStack>
                </Box>

                {/* 過去ベンチマーク参照ボタン */}
                <Box mb={4}>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    variant="outline"
                    onClick={() => setIsSearchModalOpen(true)}
                    isDisabled={!pagePermissions?.isEditable}
                  >
                    {t(`${BP}.button.searchPastBenchmark`)}
                  </Button>
                </Box>

                {/* 過去ベンチマーク検索フローティングパネル */}
                <Portal>
                  {isSearchModalOpen && (
                    <PastBenchmarkSearchPanel
                      onClose={() => setIsSearchModalOpen(false)}
                    />
                  )}
                </Portal>
              </fieldset>

              {/* ベンチマークデータ */}
              <Box>
                <Text fontWeight="bold" mb={3}>{t(`${BP}.label.benchmarkData`)}</Text>
                {/* PartNoドロップダウン（PartNoが存在する場合のみ表示） */}
                {hasPartNo && (
                  <HStack spacing={2} mb={3} justifyContent="flex-start">
                    <Text fontSize="sm" fontWeight="medium">PartNo</Text>
                    <Select
                      size="sm"
                      w="200px"
                      value={localBenchmark.selectedPartNo || ""}
                      onChange={(e) => handlePartNoChange(e.target.value)}
                    >
                      <option value="">{t('common.label.selectPlaceholder')}</option>
                      {partNoList.map((item) => (
                        <option key={item.partNo} value={item.partNo}>
                          {resolveDisplayPartNoByKey(item.partNo, partNoData)}
                        </option>
                      ))}
                    </Select>
                  </HStack>
                )}
                {/* NumberIncrementStepper/NumberDecrementStepperはネイティブbuttonではないためfieldsetのdisabledが効かず、pointerEvents:'none'で明示的にクリック不可にする */}
                <fieldset disabled={!pagePermissions?.isEditable} style={!pagePermissions?.isEditable ? { pointerEvents: 'none' } : undefined}>
                  {/* PartNoバリデーションエラー表示 */}
                  {hasPartNo && errors.partNoValidation.isError && (
                    <Text color="red.500" fontSize="sm" mb={2}>
                      {errors.partNoValidation.errorMessage}
                    </Text>
                  )}
                  {/* プロセスコード未選択時の案内（ライン入力モードでは不要） */}
                  {!productionLineId && !localBenchmark.processCode && (
                    <Text color="gray.500" fontSize="sm" my={4}>
                      {t(`${BP}.label.selectProcessCode`)}
                    </Text>
                  )}
                  {/* テーブル: プロセスコード未選択時（ライン入力モード除く） or PartNoモードで未選択時は非表示 */}
                  {(productionLineId || localBenchmark.processCode) && (!hasPartNo || localBenchmark.selectedPartNo) && <>
                    <Table variant="simple" size="sm" border="1px solid" borderColor="gray.200">
                      <Thead bg="gray.100">
                        <Tr>
                          <Th rowSpan={2} borderRight="1px solid" borderColor="gray.200" w="200px">{t('common.label.item')}</Th>
                          <Th colSpan={2} textAlign="center" borderRight="1px solid" borderColor="gray.200" borderBottom="1px solid" borderBottomColor="gray.200">{t(`${BP}.label.current`)}</Th>
                          <Th colSpan={1} textAlign="center">{t(`${BP}.label.increaseInvestment`)}</Th>
                          <Th colSpan={1} textAlign="center"></Th>
                        </Tr>
                        <Tr>
                          <Th borderRight="1px solid" borderColor="gray.200" textAlign="center" w="150px">{t(`${BP}.label.actualBest`)}</Th>
                          <Th borderRight="1px solid" borderColor="gray.200" textAlign="center" w="150px">{t(`${BP}.label.theoretical`)}</Th>
                          <Th textAlign="center" w="150px">{t(`${BP}.label.investmentPlan`)}</Th>
                          <Th textAlign="center" w="150px">{t(`${BP}.label.difference`)}</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {/* 歩留 */}
                        <Tr>
                          <Td borderRight="1px solid" borderColor="gray.200" fontWeight="semibold">{t(`${BP}.label.yield`)}</Td>
                          <Td borderRight="1px solid" borderColor="gray.200">
                            <Box display="flex" justifyContent="center" >
                              <FormControl maxW="120px" textAlign="center" isRequired isInvalid={!!fieldErrors.actualBestYield.isError}>
                                <NumberInput
                                  width="120px"
                                  min={0}
                                  max={100}
                                  precision={2}
                                  step={0.01}
                                  value={displayActualBest?.yield || ""}
                                  onChange={(_, val) => handleStateUpdate("actualBest", "yield", val)}
                                >
                                  <NumberInputField placeholder="%" textAlign="right" />
                                  <NumberInputStepper>
                                    <NumberIncrementStepper />
                                    <NumberDecrementStepper />
                                  </NumberInputStepper>
                                </NumberInput>
                                <FormErrorMessage display="flex" justifyContent="center">
                                  {fieldErrors.actualBestYield.errorMessage}
                                </FormErrorMessage>
                              </FormControl>
                            </Box>
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200">
                            <Box display="flex" justifyContent="center" >
                              <FormControl maxW="120px" textAlign="center" isRequired isInvalid={!!fieldErrors.theoreticalYield.isError}>
                                <NumberInput
                                  width="120px"
                                  min={0}
                                  max={100}
                                  precision={2}
                                  step={0.01}
                                  value={displayTheoretical?.yield || ""}
                                  onChange={(_, val) => handleStateUpdate("theoretical", "yield", val)}
                                >
                                  <NumberInputField placeholder="%" textAlign="right" />
                                  <NumberInputStepper>
                                    <NumberIncrementStepper />
                                    <NumberDecrementStepper />
                                  </NumberInputStepper>
                                </NumberInput>
                                <FormErrorMessage display="flex" justifyContent="center">
                                  {fieldErrors.theoreticalYield.errorMessage}
                                </FormErrorMessage>
                              </FormControl>
                            </Box>
                          </Td>
                          <Td>
                            <Box display="flex" justifyContent="center">
                              <FormControl maxW="120px" textAlign="center" isRequired isInvalid={!!fieldErrors.investmentPlanYield.isError}>
                                <NumberInput
                                  width="120px"
                                  min={0}
                                  max={100}
                                  precision={2}
                                  step={0.01}
                                  value={displayInvestmentPlan?.yield || ""}
                                  onChange={(_, val) => handleStateUpdate("investmentPlan", "yield", val)}
                                >
                                  <NumberInputField placeholder="%" textAlign="right" />
                                  <NumberInputStepper>
                                    <NumberIncrementStepper />
                                    <NumberDecrementStepper />
                                  </NumberInputStepper>
                                </NumberInput>
                                <FormErrorMessage display="flex" justifyContent="center">
                                  {fieldErrors.investmentPlanYield.errorMessage}
                                </FormErrorMessage>
                              </FormControl>
                            </Box>
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200" textAlign="right">
                            {formatNumber(
                              safeNumber(
                                getDifference(
                                  displayInvestmentPlan?.yield,
                                  displayActualBest?.yield,
                                  displayTheoretical?.yield
                                )
                              )
                            )}
                          </Td>
                        </Tr>

                        {/* 稼働率 */}
                        <Tr>
                          <Td borderRight="1px solid" borderColor="gray.200" fontWeight="semibold">{t(`${BP}.label.operatingRate`)}</Td>
                          <Td borderRight="1px solid" borderColor="gray.200">
                            <Box display="flex" justifyContent="center">
                              <FormControl maxW="120px" w="100%" textAlign="center" isRequired isInvalid={!!fieldErrors.actualBestOperatingRate.isError}>
                                <NumberInput
                                  width="120px"
                                  min={0}
                                  max={100}
                                  precision={1}
                                  step={0.1}
                                  value={displayActualBest?.operatingRate || ""}
                                  onChange={(_, val) => handleStateUpdate("actualBest", "operatingRate", val)}
                                >
                                  <NumberInputField placeholder="%" textAlign="right" />
                                  <NumberInputStepper>
                                    <NumberIncrementStepper />
                                    <NumberDecrementStepper />
                                  </NumberInputStepper>
                                </NumberInput>
                                <FormErrorMessage display="flex" justifyContent="center">
                                  {fieldErrors.actualBestOperatingRate.errorMessage}
                                </FormErrorMessage>
                              </FormControl>
                            </Box>
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200">
                            <Box display="flex" justifyContent="center">
                              <FormControl maxW="120px" w="100%" textAlign="center" isRequired isInvalid={!!fieldErrors.theoreticalOperatingRate.isError}>
                                <NumberInput
                                  width="120px"
                                  min={0}
                                  max={100}
                                  precision={1}
                                  step={0.1}
                                  value={displayTheoretical?.operatingRate || ""}
                                  onChange={(_, val) => handleStateUpdate("theoretical", "operatingRate", val)}
                                >
                                  <NumberInputField placeholder="%" textAlign="right" />
                                  <NumberInputStepper>
                                    <NumberIncrementStepper />
                                    <NumberDecrementStepper />
                                  </NumberInputStepper>
                                </NumberInput>
                                <FormErrorMessage display="flex" justifyContent="center">
                                  {fieldErrors.theoreticalOperatingRate.errorMessage}
                                </FormErrorMessage>
                              </FormControl>
                            </Box>
                          </Td>
                          <Td>
                            <Box display="flex" justifyContent="center">
                              <FormControl maxW="120px" w="100%" textAlign="center" isRequired isInvalid={!!fieldErrors.investmentPlanOperatingRate.isError}>
                                <NumberInput
                                  width="120px"
                                  min={0}
                                  max={100}
                                  precision={1}
                                  step={0.1}
                                  value={displayInvestmentPlan?.operatingRate || ""}
                                  onChange={(_, val) => handleStateUpdate("investmentPlan", "operatingRate", val)}
                                >
                                  <NumberInputField placeholder="%" textAlign="right" />
                                  <NumberInputStepper>
                                    <NumberIncrementStepper />
                                    <NumberDecrementStepper />
                                  </NumberInputStepper>
                                </NumberInput>
                                <FormErrorMessage display="flex" justifyContent="center">
                                  {fieldErrors.investmentPlanOperatingRate.errorMessage}
                                </FormErrorMessage>
                              </FormControl>
                            </Box>
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200" textAlign="right">
                            {formatNumber(
                              safeNumber(
                                getDifference(
                                  displayInvestmentPlan?.operatingRate,
                                  displayActualBest?.operatingRate,
                                  displayTheoretical?.operatingRate
                                )
                              )
                            )}
                          </Td>
                        </Tr>

                        {/* C/T */}
                        <Tr>
                          <Td borderRight="1px solid" borderColor="gray.200" fontWeight="semibold">{t(`${BP}.label.cycleTime`)}</Td>
                          <Td borderRight="1px solid" borderColor="gray.200">
                            <Box display="flex" justifyContent="center">
                              <FormControl maxW="120px" w="100%" textAlign="center" isRequired isInvalid={!!fieldErrors.actualBestCycleTime.isError}>
                                <NumberInput
                                  width="120px"
                                  min={0}
                                  max={100000}
                                  precision={1}
                                  step={0.1}
                                  value={displayActualBest?.cycleTime || ""}
                                  onChange={(_, val) => handleStateUpdate("actualBest", "cycleTime", val)}
                                >
                                  <NumberInputField placeholder={t(`${BP}.placeholder.seconds`)} textAlign="right" />
                                  <NumberInputStepper>
                                    <NumberIncrementStepper />
                                    <NumberDecrementStepper />
                                  </NumberInputStepper >
                                </NumberInput>
                                <FormErrorMessage display="flex" justifyContent="center">
                                  {fieldErrors.actualBestCycleTime.errorMessage}
                                </FormErrorMessage>
                              </FormControl>
                            </Box>
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200">
                            <Box display="flex" justifyContent="center">
                              <FormControl maxW="120px" w="100%" textAlign="center" isRequired isInvalid={!!fieldErrors.theoreticalCycleTime.isError}>
                                <NumberInput
                                  width="120px"
                                  min={0}
                                  max={100000}
                                  precision={1}
                                  step={0.1}
                                  value={
                                    displayTheoretical?.cycleTime ||
                                    (hasPartNo && localBenchmark.selectedPartNo
                                      ? getCycleTimeByProcessIdAndPartNo(pastUniqueProcess, localBenchmark.processCode, localBenchmark.selectedPartNo)
                                      : getCycleTimeByProcessId(pastUniqueProcess, localBenchmark.processCode)
                                    ) ||
                                    ""
                                  }
                                  onChange={(_, val) => handleStateUpdate("theoretical", "cycleTime", val)}
                                >
                                  <NumberInputField placeholder={t(`${BP}.placeholder.seconds`)} textAlign="right" />
                                  <NumberInputStepper>
                                    <NumberIncrementStepper />
                                    <NumberDecrementStepper />
                                  </NumberInputStepper>
                                </NumberInput>
                                <FormErrorMessage display="flex" justifyContent="center">
                                  {fieldErrors.theoreticalCycleTime.errorMessage}
                                </FormErrorMessage>
                              </FormControl>
                            </Box>
                          </Td>
                          <Td>
                            <Box display="flex" justifyContent="center">
                              <FormControl maxW="120px" w="100%" textAlign="center" isRequired isInvalid={!!fieldErrors.investmentPlanCycleTime.isError}>
                                <NumberInput
                                  width="120px"
                                  min={0}
                                  max={100000}
                                  precision={1}
                                  step={0.1}
                                  value={
                                    displayInvestmentPlan?.cycleTime ||
                                    (hasPartNo && localBenchmark.selectedPartNo
                                      ? getCycleTimeByProcessIdAndPartNo(processCodeData, localBenchmark.processCode, localBenchmark.selectedPartNo)
                                      : getCycleTimeByProcessId(processCodeData, localBenchmark.processCode)
                                    ) ||
                                    ""
                                  }
                                  onChange={(_, val) => handleStateUpdate("investmentPlan", "cycleTime", val)}
                                >
                                  <NumberInputField placeholder={t(`${BP}.placeholder.seconds`)} textAlign="right" />
                                  <NumberInputStepper>
                                    <NumberIncrementStepper />
                                    <NumberDecrementStepper />
                                  </NumberInputStepper>
                                </NumberInput>
                                <FormErrorMessage display="flex" justifyContent="center">
                                  {fieldErrors.investmentPlanCycleTime.errorMessage}
                                </FormErrorMessage>
                              </FormControl>
                            </Box>
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200" textAlign="right">
                            {formatNumber(
                              safeNumber(
                                getCicleTimeDifference(
                                  displayInvestmentPlan?.cycleTime,
                                  displayActualBest?.cycleTime,
                                  displayTheoretical?.cycleTime
                                )
                              )
                            )}
                          </Td>
                        </Tr>

                        {/* 1日あたり必要人員数 */}
                        <Tr>
                          <Td borderRight="1px solid" borderColor="gray.200" fontWeight="semibold">{t(`${BP}.label.personnelPerDay`)}</Td>
                          <Td borderRight="1px solid" borderColor="gray.200">
                            <Box display="flex" justifyContent="center">
                              <FormControl maxW="120px" w="100%" textAlign="center" isRequired isInvalid={!!fieldErrors.actualBestPersonnelPerDay.isError}>
                                <NumberInput
                                  width="120px"
                                  min={0}
                                  max={100000}
                                  precision={1}
                                  step={0.1}
                                  value={Number.isNaN(displayActualBest?.personnelPerDay) ? 0 : (displayActualBest?.personnelPerDay?? "")}
                                  onChange={(_, val) => handleStateUpdate("actualBest", "personnelPerDay", val)}
                                >
                                  <NumberInputField placeholder={t(`${BP}.placeholder.person`)} textAlign="right" />
                                  <NumberInputStepper>
                                    <NumberIncrementStepper />
                                    <NumberDecrementStepper />
                                  </NumberInputStepper>
                                </NumberInput>
                                <FormErrorMessage display="flex" justifyContent="center">
                                  {fieldErrors.actualBestPersonnelPerDay.errorMessage}
                                </FormErrorMessage>
                              </FormControl>
                            </Box>
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200">
                            <Box display="flex" justifyContent="center">
                              <FormControl maxW="120px" w="100%" textAlign="center" isRequired isInvalid={!!fieldErrors.theoreticalPersonnelPerDay.isError}>
                                <NumberInput
                                  width="120px"
                                  min={0}
                                  max={100000}
                                  precision={1}
                                  step={0.1}
                                  value={Number.isNaN(displayTheoretical?.personnelPerDay) ? 0 : (displayTheoretical?.personnelPerDay ?? "")}
                                  onChange={(_, val) => handleStateUpdate("theoretical", "personnelPerDay", val)}
                                >
                                  <NumberInputField placeholder={t(`${BP}.placeholder.person`)} textAlign="right" />
                                  <NumberInputStepper>
                                    <NumberIncrementStepper />
                                    <NumberDecrementStepper />
                                  </NumberInputStepper>
                                </NumberInput>
                                <FormErrorMessage display="flex" justifyContent="center">
                                  {fieldErrors.theoreticalPersonnelPerDay.errorMessage}
                                </FormErrorMessage>
                              </FormControl>
                            </Box>
                          </Td>
                          <Td>
                            <Box display="flex" justifyContent="center">
                              <FormControl maxW="120px" w="100%" textAlign="center" isRequired isInvalid={!!fieldErrors.investmentPlanPersonnelPerDay.isError}>
                                <NumberInput
                                  width="120px"
                                  min={0}
                                  max={100000}
                                  precision={1}
                                  step={0.1}
                                  value={Number.isNaN(displayInvestmentPlan?.personnelPerDay) ? 0 : (displayInvestmentPlan?.personnelPerDay ?? "")}
                                  onChange={(_, val) => handleStateUpdate("investmentPlan", "personnelPerDay", val)}
                                >
                                  <NumberInputField placeholder={t(`${BP}.placeholder.person`)} textAlign="right" />
                                  <NumberInputStepper>
                                    <NumberIncrementStepper />
                                    <NumberDecrementStepper />
                                  </NumberInputStepper>
                                </NumberInput>
                                <FormErrorMessage display="flex" justifyContent="center">
                                  {fieldErrors.investmentPlanPersonnelPerDay.errorMessage}
                                </FormErrorMessage>
                              </FormControl>
                            </Box>
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200" textAlign="right">
                            {formatNumber(
                              safeNumber(
                                getDifference(
                                  displayInvestmentPlan?.personnelPerDay,
                                  displayActualBest?.personnelPerDay,
                                  displayTheoretical?.personnelPerDay
                                )
                              )
                            )}
                          </Td>
                        </Tr>

                        {/* 生産数 */}
                        <Tr bg="gray.50">
                          <Td borderRight="1px solid" borderColor="gray.200" fontWeight="bold">{t(`${BP}.label.productionVolume`)}</Td>
                          <Td borderRight="1px solid" borderColor="gray.200" textAlign="right">
                            {formatNumber(safeNumber(displayActualBest?.productionVolume))}
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200" textAlign="right">
                            {formatNumber(safeNumber(displayTheoretical?.productionVolume))}
                          </Td>
                          <Td textAlign="right">
                            {formatNumber(safeNumber(displayInvestmentPlan?.productionVolume))}
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200" textAlign="right">
                            {formatNumber(
                              safeNumber(
                                getDifference(
                                  displayInvestmentPlan?.productionVolume,
                                  displayActualBest?.productionVolume,
                                  displayTheoretical?.productionVolume
                                )
                              )
                            )}
                          </Td>
                        </Tr>

                        {/* 設備あたり生産性 */}
                        <Tr bg="gray.50">
                          <Td borderRight="1px solid" borderColor="gray.200" fontWeight="bold">
                            <HStack spacing={1}>
                              <Text fontWeight="bold">{t(`${BP}.label.productionPerEquipment`)}</Text>
                              <Popover placement="auto">
                                <PopoverTrigger>
                                  <Button
                                    style={{ background: 'none', border: 'none', padding: 0 }}
                                    minW="auto"
                                    h="auto"
                                    ml={1}
                                  >
                                    <FaRegQuestionCircle size="16px" cursor="pointer" />
                                  </Button>
                                </PopoverTrigger>
                                <Portal>
                                  <PopoverContent width="fit-content" bgColor="#fffff0">
                                    <PopoverArrow />
                                    <PopoverBody>
                                      <Text fontSize="sm">{t(`${BP}.formula.productionPerEquipment`)}</Text>
                                    </PopoverBody>
                                  </PopoverContent>
                                </Portal>
                              </Popover>
                            </HStack>
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200" textAlign="right">
                            {formatNumber(safeNumber(displayActualBest?.productionPerEquipment))}
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200" textAlign="right">
                            {formatNumber(safeNumber(displayTheoretical?.productionPerEquipment))}
                          </Td>
                          <Td textAlign="right">
                            {formatNumber(safeNumber(displayInvestmentPlan?.productionPerEquipment))}
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200" textAlign="right">
                            {formatNumber(
                              safeNumber(
                                getDifference(
                                  displayInvestmentPlan?.productionPerEquipment,
                                  displayActualBest?.productionPerEquipment,
                                  displayTheoretical?.productionPerEquipment
                                )
                              )
                            )}
                          </Td>
                        </Tr>

                        {/* 1人あたり生産性 */}
                        <Tr bg="gray.50">
                          <Td borderRight="1px solid" borderColor="gray.200" fontWeight="bold">
                            <HStack spacing={1}>
                              <Text fontWeight="bold">{t(`${BP}.label.productionPerPerson`)}</Text>
                              <Popover placement="auto">
                                <PopoverTrigger>
                                  <Button
                                    style={{ background: 'none', border: 'none', padding: 0 }}
                                    minW="auto"
                                    h="auto"
                                    ml={1}
                                  >
                                    <FaRegQuestionCircle size="16px" cursor="pointer" />
                                  </Button>
                                </PopoverTrigger>
                                <Portal>
                                  <PopoverContent width="fit-content" bgColor="#fffff0">
                                    <PopoverArrow />
                                    <PopoverBody>
                                      <Text fontSize="sm">{t(`${BP}.formula.productionPerPerson`)}</Text>
                                    </PopoverBody>
                                  </PopoverContent>
                                </Portal>
                              </Popover>
                            </HStack>
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200" textAlign="right">
                            {formatNumber(safeNumber(displayActualBest?.productionPerPerson))}
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200" textAlign="right">
                            {formatNumber(safeNumber(displayTheoretical?.productionPerPerson))}
                          </Td>
                          <Td textAlign="right">
                            {formatNumber(safeNumber(displayInvestmentPlan?.productionPerPerson))}
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200" textAlign="right">
                            {formatNumber(
                              safeNumber(
                                getDifference(
                                  displayInvestmentPlan?.productionPerPerson,
                                  displayActualBest?.productionPerPerson,
                                  displayTheoretical?.productionPerPerson
                                )
                              )) || 0}
                          </Td>
                        </Tr>
                      </Tbody>
                    </Table>
                    {/* 注意書き（*：理論値・目論見の前提／算出基準の補足） */}
                    <Box mt={2} bg="gray.50" p={2} borderRadius="md" fontSize="xs" color="gray.600">
                      <Text textAlign="left">{t(`${BP}.label.noteTheoretical`)}</Text>
                      <Text textAlign="left">{t(`${BP}.label.note24h`)}</Text>
                    </Box>
                  </>}
                </fieldset>
              </Box>

              {/* フリー入力フィールド */}
              <fieldset disabled={!pagePermissions?.isEditable} style={!pagePermissions?.isEditable ? { pointerEvents: 'none' } : undefined}>
                <Box>
                  <Text fontWeight="bold" mb={2}>{t(`${BP}.label.analysisSection`)}</Text>
                  <VStack align="stretch" spacing={2}>
                    <FormControl isRequired isInvalid={!!errors.gapAnalysis.isError}>
                      <FormLabel fontSize="sm">{t(`${BP}.label.gapAnalysis`)}</FormLabel>
                      <Textarea
                        value={localBenchmark.freeInputFields?.gapAnalysis || ""}
                        onChange={(e) => handleFreeFieldUpdate("gapAnalysis", e.target.value)}
                        rows={3}
                        placeholder={t(`${BP}.placeholder.freeInput`)}
                      />
                      <FormErrorMessage>{errors.gapAnalysis.errorMessage}</FormErrorMessage>
                    </FormControl>
                    <FormControl isRequired isInvalid={!!errors.productionPerEquipment.isError}>
                      <FormLabel fontSize="sm">{t(`${BP}.label.productionPerEquipmentMeasures`)}</FormLabel>
                      <Textarea
                        value={localBenchmark.freeInputFields?.productionPerEquipment || ""}
                        onChange={(e) => handleFreeFieldUpdate("productionPerEquipment", e.target.value)}
                        rows={2}
                        placeholder={t(`${BP}.placeholder.freeInput`)}
                      />
                      <FormErrorMessage>{errors.productionPerEquipment.errorMessage}</FormErrorMessage>
                    </FormControl>
                    <FormControl isRequired isInvalid={!!errors.productionPerPerson.isError}>
                      <FormLabel fontSize="sm">{t(`${BP}.label.productionPerPersonMeasures`)}</FormLabel>
                      <Textarea
                        value={localBenchmark.freeInputFields?.productionPerPerson || ""}
                        onChange={(e) => handleFreeFieldUpdate("productionPerPerson", e.target.value)}
                        rows={2}
                        placeholder={t(`${BP}.placeholder.freeInput`)}
                      />
                      <FormErrorMessage>{errors.productionPerPerson.errorMessage}</FormErrorMessage>
                    </FormControl>
                    <FormControl isRequired isInvalid={!!errors.nonOperationMeasures.isError}>
                      <FormLabel fontSize="sm">{t(`${BP}.label.nonOperationMeasures`)}</FormLabel>
                      <Textarea
                        value={localBenchmark.freeInputFields?.nonOperationMeasures || ""}
                        onChange={(e) => handleFreeFieldUpdate("nonOperationMeasures", e.target.value)}
                        rows={2}
                        placeholder={t(`${BP}.placeholder.freeInput`)}
                      />
                      <FormErrorMessage>{errors.nonOperationMeasures.errorMessage}</FormErrorMessage>
                    </FormControl>
                    <FormControl isRequired isInvalid={!!errors.yieldMeasures.isError}>
                      <FormLabel fontSize="sm">{t(`${BP}.label.yieldMeasures`)}</FormLabel>
                      <Textarea
                        value={localBenchmark.freeInputFields?.yieldMeasures || ""}
                        onChange={(e) => handleFreeFieldUpdate("yieldMeasures", e.target.value)}
                        rows={2}
                        placeholder={t(`${BP}.placeholder.freeInput`)}
                      />
                      <FormErrorMessage>{errors.yieldMeasures.errorMessage}</FormErrorMessage>
                    </FormControl>
                  </VStack>
                  {/* 注意書き（***） */}
                  <Box mt={2} bg="gray.50" p={2} borderRadius="md" fontSize="xs" color="gray.600">
                    <Text textAlign="left">{t(`${BP}.label.noteGapAnalysis`)}</Text>
                  </Box>
                </Box>
              </fieldset>

              {/* 保存ボタン */}
              <Flex justify="flex-end" gap={2}>
                {pagePermissions?.isEditable && (
                  <>
                    <Button colorScheme="teal" variant="outline" onClick={handleTempSave}>{t(`${BP}.button.tempSave`)}</Button>
                    <Button colorScheme="teal" variant="outline" onClick={handleTempLoad}>{t(`${BP}.button.tempLoad`)}</Button>
                  </>
                )}
                <Button colorScheme="blue"
                  isDisabled={isSaving || !pagePermissions?.isEditable}
                  onClick={() => {
                    if (validate() === false) {
                      toast({
                        title: TITLE,
                        description: t(`${BP}.validation.inputError`),
                        status: 'warning',
                        duration: 2000,
                        isClosable: true,
                      });
                      return;
                    }
                    handleSave();
                  }}>
                  {t('common.button.save')}
                </Button>
                <Button onClick={handleModalClose}>
                  {t('common.button.close')}
                </Button>
              </Flex>
            </VStack>
          </Box>
        </Box>
      )}
    </>
  );
};

export default BenchmarkInputPopover;
